export interface DiagramViewport {
  scale: number
  translateX: number
  translateY: number
}

export interface DiagramPoint {
  x: number
  y: number
}

export interface DiagramSize {
  width: number
  height: number
}

export interface DiagramObjOccurrencePort {
  x?: number
  y?: number
  side?: string
}

export interface DiagramObjOccurrence {
  id: string
  entityId: string
  position: DiagramPoint
  size?: DiagramSize
  angle?: number
  z?: number
  shape?: string
  style?: Record<string, unknown>
  ports?: Record<string, DiagramObjOccurrencePort>
  label?: string
}

export interface DiagramCnxOccurrenceLabel {
  id: string
  text: string
  position?: number
}

export interface DiagramCnxOccurrence {
  id: string
  entityId?: string
  sourceOccurrenceId: string
  targetOccurrenceId: string
  sourcePortId?: string
  targetPortId?: string
  vertices?: DiagramPoint[]
  labels?: DiagramCnxOccurrenceLabel[]
  style?: Record<string, unknown>
  z?: number
}

export interface DiagramTextOccurrence {
  id: string
  text: string
  position: DiagramPoint
  size?: DiagramSize
  style?: Record<string, unknown>
  z?: number
}

export interface DiagramOccurrences {
  objs: Record<string, DiagramObjOccurrence>
  cnxs: Record<string, DiagramCnxOccurrence>
  texts?: Record<string, DiagramTextOccurrence>
}

export interface DiagramDocumentV1 extends Record<string, unknown> {
  version: 1
  viewport: DiagramViewport
  occurrences: DiagramOccurrences
  order?: string[]
}

export type DiagramDocument = DiagramDocumentV1

export type DiagramOccurrenceType = 'obj' | 'cnx' | 'text'

export interface DiagramPatchOperation {
  op:
    | 'set_viewport'
    | 'put_obj_occurrence'
    | 'patch_obj_occurrence'
    | 'delete_obj_occurrence'
    | 'put_cnx_occurrence'
    | 'patch_cnx_occurrence'
    | 'delete_cnx_occurrence'
    | 'put_text_occurrence'
    | 'patch_text_occurrence'
    | 'delete_text_occurrence'
    | 'reorder'
  occurrenceType?: DiagramOccurrenceType
  occurrenceId?: string
  value?: unknown
}

export function createEmptyDiagramDocument(): DiagramDocumentV1 {
  return {
    version: 1,
    viewport: {
      scale: 1,
      translateX: 0,
      translateY: 0
    },
    occurrences: {
      objs: {},
      cnxs: {}
    }
  }
}

export function isDiagramDocument(value: unknown): value is DiagramDocument {
  if (!isRecord(value) || value.version !== 1) {
    return false
  }

  const viewport = value.viewport
  const occurrences = value.occurrences

  return (
    isRecord(viewport) &&
    typeof viewport.scale === 'number' &&
    typeof viewport.translateX === 'number' &&
    typeof viewport.translateY === 'number' &&
    isRecord(occurrences) &&
    isRecord(occurrences.objs) &&
    isRecord(occurrences.cnxs) &&
    (occurrences.texts === undefined || isRecord(occurrences.texts)) &&
    (value.order === undefined ||
      (Array.isArray(value.order) && value.order.every((item) => typeof item === 'string')))
  )
}

export function applyDiagramPatch(
  document: DiagramDocument,
  patch: DiagramPatchOperation[]
): DiagramDocument {
  const nextDocument = cloneDiagramDocument(document)
  for (const operation of patch) {
    mutateDiagramDocument(nextDocument, operation)
  }
  return nextDocument
}

export function applyDiagramPatchOperation(
  document: DiagramDocument,
  operation: DiagramPatchOperation
): DiagramDocument {
  const nextDocument = cloneDiagramDocument(document)
  mutateDiagramDocument(nextDocument, operation)
  return nextDocument
}

function cloneDiagramDocument(document: DiagramDocument): DiagramDocument {
  return {
    version: document.version,
    viewport: { ...document.viewport },
    occurrences: {
      objs: { ...document.occurrences.objs },
      cnxs: { ...document.occurrences.cnxs },
      ...(document.occurrences.texts
        ? { texts: { ...document.occurrences.texts } }
        : {})
    },
    ...(document.order ? { order: [...document.order] } : {})
  }
}

function mutateDiagramDocument(
  document: DiagramDocument,
  operation: DiagramPatchOperation
): void {
  switch (operation.op) {
    case 'set_viewport': {
      if (!isRecord(operation.value)) {
        throw new Error('set_viewport requires a viewport object value')
      }
      document.viewport = mergeObjects(document.viewport, operation.value)
      return
    }
    case 'put_obj_occurrence': {
      const occurrence = getOperationValue<DiagramObjOccurrence>(operation)
      const occurrenceId = resolveOccurrenceId(operation, occurrence)
      document.occurrences.objs[occurrenceId] = {
        ...occurrence,
        id: occurrenceId
      }
      ensureOccurrenceInOrder(document, occurrenceId)
      return
    }
    case 'patch_obj_occurrence': {
      const occurrenceId = resolveOccurrenceId(operation)
      const existing = document.occurrences.objs[occurrenceId]
      if (!existing) {
        throw new Error(`Cannot patch missing object occurrence '${occurrenceId}'`)
      }
      document.occurrences.objs[occurrenceId] = mergeObjects(
        existing,
        getOperationValue<Record<string, unknown>>(operation)
      )
      return
    }
    case 'delete_obj_occurrence': {
      const occurrenceId = resolveOccurrenceId(operation)
      delete document.occurrences.objs[occurrenceId]
      removeOccurrenceFromOrder(document, occurrenceId)
      return
    }
    case 'put_cnx_occurrence': {
      const occurrence = getOperationValue<DiagramCnxOccurrence>(operation)
      const occurrenceId = resolveOccurrenceId(operation, occurrence)
      document.occurrences.cnxs[occurrenceId] = {
        ...occurrence,
        id: occurrenceId
      }
      ensureOccurrenceInOrder(document, occurrenceId)
      return
    }
    case 'patch_cnx_occurrence': {
      const occurrenceId = resolveOccurrenceId(operation)
      const existing = document.occurrences.cnxs[occurrenceId]
      if (!existing) {
        throw new Error(`Cannot patch missing connection occurrence '${occurrenceId}'`)
      }
      document.occurrences.cnxs[occurrenceId] = mergeObjects(
        existing,
        getOperationValue<Record<string, unknown>>(operation)
      )
      return
    }
    case 'delete_cnx_occurrence': {
      const occurrenceId = resolveOccurrenceId(operation)
      delete document.occurrences.cnxs[occurrenceId]
      removeOccurrenceFromOrder(document, occurrenceId)
      return
    }
    case 'put_text_occurrence': {
      const occurrence = getOperationValue<DiagramTextOccurrence>(operation)
      const occurrenceId = resolveOccurrenceId(operation, occurrence)
      if (!document.occurrences.texts) {
        document.occurrences.texts = {}
      }
      document.occurrences.texts[occurrenceId] = {
        ...occurrence,
        id: occurrenceId
      }
      ensureOccurrenceInOrder(document, occurrenceId)
      return
    }
    case 'patch_text_occurrence': {
      const occurrenceId = resolveOccurrenceId(operation)
      const existing = document.occurrences.texts?.[occurrenceId]
      if (!existing) {
        throw new Error(`Cannot patch missing text occurrence '${occurrenceId}'`)
      }
      document.occurrences.texts = {
        ...(document.occurrences.texts || {}),
        [occurrenceId]: mergeObjects(
          existing,
          getOperationValue<Record<string, unknown>>(operation)
        )
      }
      return
    }
    case 'delete_text_occurrence': {
      const occurrenceId = resolveOccurrenceId(operation)
      if (document.occurrences.texts) {
        delete document.occurrences.texts[occurrenceId]
        if (Object.keys(document.occurrences.texts).length === 0) {
          delete document.occurrences.texts
        }
      }
      removeOccurrenceFromOrder(document, occurrenceId)
      return
    }
    case 'reorder': {
      if (!Array.isArray(operation.value) || !operation.value.every((id) => typeof id === 'string')) {
        throw new Error('reorder requires a string[] value')
      }
      document.order = [...operation.value]
      return
    }
  }
}

function ensureOccurrenceInOrder(document: DiagramDocument, occurrenceId: string): void {
  if (!document.order || document.order.includes(occurrenceId)) {
    return
  }
  document.order = [...document.order, occurrenceId]
}

function removeOccurrenceFromOrder(document: DiagramDocument, occurrenceId: string): void {
  if (!document.order) {
    return
  }
  document.order = document.order.filter((id) => id !== occurrenceId)
}

function resolveOccurrenceId(
  operation: DiagramPatchOperation,
  value?: { id?: string }
): string {
  const occurrenceId = operation.occurrenceId || value?.id
  if (!occurrenceId) {
    throw new Error(`Operation '${operation.op}' requires an occurrenceId`)
  }
  return occurrenceId
}

function getOperationValue<T>(operation: DiagramPatchOperation): T {
  if (operation.value === undefined) {
    throw new Error(`Operation '${operation.op}' requires a value`)
  }
  return operation.value as T
}

function isRecord(value: unknown): value is Record<string, any> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function mergeObjects<T>(current: T, patch: unknown): T {
  if (!isRecord(current) || !isRecord(patch)) {
    return patch as T
  }

  const merged: Record<string, unknown> = { ...current }
  for (const [key, value] of Object.entries(patch)) {
    const existingValue = merged[key]
    merged[key] =
      isRecord(existingValue) && isRecord(value)
        ? mergeObjects(existingValue, value)
        : value
  }
  return merged as T
}
